import { type LogicGroup, toLogicGroups } from "./conditionLogic";
import { isDanglingReference } from "./params";
import type {
    Condition,
    EditorMetadata,
    ParamSpec,
    Transition,
    TransitionAction,
} from "./types";

/**
 * A readable rendering of everything a transition is configured to do.
 *
 * Read-only, and deliberately separate from the editing panel: the panel is a set of controls,
 * which is the wrong shape for answering "what does this actually do?". Conditions are drawn as
 * nested logic rather than printed as a string, because the nesting and which operator joins
 * what is the part that is hard to hold in your head.
 */

function LogicGroupView({
    group,
    depth,
}: {
    group: LogicGroup;
    depth: number;
}) {
    if (group.matchesEverything) {
        return (
            <div className="aer-wf-logic__group aer-wf-logic__group--vacuous">
                <span className="aer-wf-logic__warn">
                    empty group — matches every event
                </span>
            </div>
        );
    }

    return (
        <div className="aer-wf-logic__group">
            {group.nodes.map((node, index) => (
                <div
                    className="aer-wf-logic__row"
                    key={
                        node.kind === "group"
                            ? node.group.id
                            : `${node.kind}-${node.text}`
                    }
                >
                    {/* The joining operator sits between lines, so the reading order matches
                        the evaluation: everything in a group is combined by this one word. */}
                    <span className="aer-wf-logic__op">
                        {index === 0 ? "" : group.operator}
                    </span>
                    {node.kind === "group" ? (
                        <LogicGroupView group={node.group} depth={depth + 1} />
                    ) : (
                        <span
                            className={`aer-wf-logic__leaf${
                                node.kind === "filter"
                                    ? " aer-wf-logic__leaf--filter"
                                    : ""
                            }`}
                        >
                            {node.text}
                        </span>
                    )}
                </div>
            ))}
        </div>
    );
}

export function ConditionLogic({
    condition,
    metadata,
}: {
    condition: Condition;
    metadata: EditorMetadata;
}) {
    const groups = toLogicGroups(
        condition.groups,
        metadata.dataProviders,
        metadata.geoAreas,
    );

    if (!groups.length) {
        return (
            <p className="aer-wf-logic__warn">
                No groups — this condition never matches, so nothing using it
                fires.
            </p>
        );
    }

    return (
        <div className="aer-wf-logic">
            {groups.map((group, index) => (
                <div key={group.id}>
                    {/* Root groups are OR'd by the evaluator. */}
                    {index > 0 && (
                        <div className="aer-wf-logic__root-op">OR</div>
                    )}
                    <LogicGroupView group={group} depth={0} />
                </div>
            ))}
        </div>
    );
}

function paramText(spec: ParamSpec | undefined, value: unknown): string {
    if (spec?.isReference) {
        const label = spec.choices.find(
            (choice) => choice.value === String(value),
        )?.label;
        return label ?? `${String(value)} — not available to this company`;
    }
    return String(value);
}

function RuleSummary({
    config,
    index,
    metadata,
}: {
    config: TransitionAction;
    index: number;
    metadata: EditorMetadata;
}) {
    const rule = config.rules[index];
    const specs = new Map(config.paramSpec.map((spec) => [spec.name, spec]));
    const params = Object.entries(rule.extraParams ?? {});
    const shadowed = config.rules
        .slice(0, index)
        .some((earlier) => earlier.condition === null);

    return (
        <li className="aer-wf-summary__rule">
            <div className="aer-wf-summary__rule-head">
                <span className="aer-wf-summary__ordinal">{index + 1}</span>
                {rule.condition ? (
                    <span>if</span>
                ) : (
                    <span className="aer-wf-summary__always">always</span>
                )}
                {shadowed && (
                    <span className="aer-wf-badge aer-wf-badge--danger">
                        never reached
                    </span>
                )}
            </div>

            {rule.condition && (
                <ConditionLogic
                    condition={rule.condition}
                    metadata={metadata}
                />
            )}

            <div className="aer-wf-summary__then">
                <span className="aer-wf-summary__arrow">→</span>
                {params.length ? (
                    <span>
                        {params.map(([name, value], position) => {
                            const spec = specs.get(name);
                            return (
                                <span key={name}>
                                    {position > 0 && ", "}
                                    <code>{name}</code> ={" "}
                                    <span
                                        className={
                                            spec &&
                                            isDanglingReference(spec, value)
                                                ? "aer-wf-summary__dangling"
                                                : undefined
                                        }
                                    >
                                        {paramText(spec, value)}
                                    </span>
                                </span>
                            );
                        })}
                    </span>
                ) : (
                    <span className="aer-wf-summary__muted">
                        no parameters — the action uses its own defaults
                    </span>
                )}
            </div>
        </li>
    );
}

export function TransitionSummary({
    transition,
    metadata,
}: {
    transition: Transition;
    metadata: EditorMetadata;
}) {
    return (
        <div className="aer-wf-summary">
            <section>
                <h4 className="aer-wf-summary__heading">Fires when</h4>
                {transition.condition ? (
                    <ConditionLogic
                        condition={transition.condition}
                        metadata={metadata}
                    />
                ) : (
                    <p className="aer-wf-summary__muted">
                        {transition.transitionType === "MANUAL"
                            ? "A person triggers it."
                            : "Always, once an event reaches this status."}
                    </p>
                )}
            </section>

            <section>
                <h4 className="aer-wf-summary__heading">
                    Then, in order
                    {transition.actionConfigs.length === 0 && " — nothing"}
                </h4>

                {transition.actionConfigs.length > 0 && (
                    <ol className="aer-wf-summary__actions">
                        {transition.actionConfigs.map((config) => (
                            <li key={config.id}>
                                <div className="aer-wf-summary__action-head">
                                    <strong>{config.title}</strong>
                                    {!config.isRegistered && (
                                        <span className="aer-wf-badge aer-wf-badge--danger">
                                            not registered — skipped
                                        </span>
                                    )}
                                </div>

                                {config.rules.length ? (
                                    <ol className="aer-wf-summary__rules">
                                        {config.rules.map((rule, index) => (
                                            <RuleSummary
                                                key={rule.id}
                                                config={config}
                                                index={index}
                                                metadata={metadata}
                                            />
                                        ))}
                                    </ol>
                                ) : (
                                    <p className="aer-wf-summary__muted">
                                        No rules — runs with no parameters.
                                    </p>
                                )}
                            </li>
                        ))}
                    </ol>
                )}
            </section>
        </div>
    );
}
